import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import resolveResponse from 'contentful-resolve-response';
import { getLang } from '@gif/shared-global';
import { HeadersType } from '../types/common';
import { ContentfulSpaceConfig } from '../types/contentful';

type ContentfulLang = 'en-US' | 'es-US';

interface ContentfulArgs {
  spaceConfig: ContentfulSpaceConfig;
  language?: ContentfulLang;
  headers?: HeadersType;
}

interface ContentfulObject {
  fields: {
    key: string;
    dictionaryValue: string;
  };
}

enum LanguageEnums {
  EN = 'en-US',
  ES = 'es-US',
}

const lang = getLang() === 'es' ? LanguageEnums.ES : LanguageEnums.EN;

const generateContentfulQueryObject = (arg: ContentfulArgs) => {
  const { language, spaceConfig, headers } = arg;
  return {
    url: `${spaceConfig.baseUrl}/spaces/${spaceConfig.spaceId}/environments/${spaceConfig.environment}/entries`,
    params: {
      locale: language || lang,
      access_token: spaceConfig.accessToken,
      content_type: spaceConfig.contentType,
      'fields.slug': spaceConfig.slug,
    },
    headers,
  };
};

export const contentfulApi = createApi({
  reducerPath: 'contentfulApi',
  baseQuery: fetchBaseQuery(),
  endpoints: (builder) => ({
    getContentfulSurfaceContent: builder.query({
      query: generateContentfulQueryObject,
      transformResponse: (response) => {
        const data = resolveResponse(response)[0].fields;
        return {
          ...data,
          heroImage: `https:${data.heroImage.fields.file.url}`,
          heroLogoOverlay: `https:${data.heroLogoOverlay.fields.file.url}`,
        };
      },
    }),
    getContentfulAvailableResidences: builder.query({
      query: generateContentfulQueryObject,
      transformResponse: (response) => {
        const data = resolveResponse(response)[0].fields;
        return {
          ...data,
          countries: data.countries.map((country: ContentfulObject) => ({
            countryCode: country.fields.key,
            name: country.fields.dictionaryValue,
          })),
          provinces: data.provinces.map((province: ContentfulObject) => ({
            countryCode: province.fields.key,
            name: province.fields.dictionaryValue,
          })),
        };
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints - must lead with "use" and end in "Query"
export const {
  useGetContentfulSurfaceContentQuery,
  useGetContentfulAvailableResidencesQuery,
} = contentfulApi;
